import { useState, useEffect, useCallback, useRef } from 'react';
import { ref, onValue, set, update } from 'firebase/database';
import { db } from '../services/firebase';
import { User } from 'firebase/auth';
import { ACHIEVEMENT_COINS } from '../lib/constants';
import type { AchievementsData } from '../lib/progression';

// === Achievement definition ===

export type AchievementConditionType =
  | 'games_played'
  | 'wins'
  | 'streak'
  | 'scopa'
  | 'cards_captured'
  | 'sbarazzo'
  | 'primiera'
  | 'tournament_win'
  | 'level';

export interface AchievementDef {
  id: string;
  name: string;
  description: string;
  conditionType: AchievementConditionType;
  conditionValue: number; // e.g. streak_3 needs streak >= 3
  coinReward: number;
}

export const ACHIEVEMENTS: AchievementDef[] = [
  {
    id: 'first_win',
    name: 'Prima vittoria',
    description: 'Vinci la tua prima partita',
    conditionType: 'wins',
    conditionValue: 1,
    coinReward: ACHIEVEMENT_COINS.first_win,
  },
  {
    id: 'streak_3',
    name: 'InSerie',
    description: 'Ottieni 3 vittorie consecutive',
    conditionType: 'streak',
    conditionValue: 3,
    coinReward: ACHIEVEMENT_COINS.streak_3,
  },
  {
    id: 'streak_5',
    name: 'SuperSerie',
    description: 'Ottieni 5 vittorie consecutive',
    conditionType: 'streak',
    conditionValue: 5,
    coinReward: ACHIEVEMENT_COINS.streak_5,
  },
  {
    id: 'streak_10',
    name: 'Leggenda',
    description: 'Ottieni 10 vittorie consecutive',
    conditionType: 'streak',
    conditionValue: 10,
    coinReward: ACHIEVEMENT_COINS.streak_10,
  },
  {
    id: 'cards_100',
    name: 'Raccoglitore',
    description: 'Cattura 100 carte in totale',
    conditionType: 'cards_captured',
    conditionValue: 100,
    coinReward: ACHIEVEMENT_COINS.cards_100,
  },
  {
    id: 'scopa_10',
    name: 'Scopa maestro',
    description: 'Fai 10 scope in totale',
    conditionType: 'scopa',
    conditionValue: 10,
    coinReward: ACHIEVEMENT_COINS.scopa_10,
  },
  {
    id: 'sbarazzo_5',
    name: 'Sbarazzatore',
    description: 'Dichiara sbarazzo 5 volte',
    conditionType: 'sbarazzo',
    conditionValue: 5,
    coinReward: ACHIEVEMENT_COINS.sbarazzo_5,
  },
  {
    id: 'primiera_10',
    name: 'Primiera Perfetta',
    description: 'Vinci la primiera 10 volte',
    conditionType: 'primiera',
    conditionValue: 10,
    coinReward: ACHIEVEMENT_COINS.primiera_10,
  },
  {
    id: 'tournament_win',
    name: 'Campione',
    description: 'Vinci un torneo',
    conditionType: 'tournament_win',
    conditionValue: 1,
    coinReward: ACHIEVEMENT_COINS.tournament_win,
  },
  {
    id: 'level_25',
    name: 'Livello 25',
    description: 'Raggiungi il livello 25',
    conditionType: 'level',
    conditionValue: 25,
    coinReward: ACHIEVEMENT_COINS.level_25,
  },
  {
    id: 'level_50',
    name: 'Maestro Assoluto',
    description: 'Raggiungi il livello 50',
    conditionType: 'level',
    conditionValue: 50,
    coinReward: ACHIEVEMENT_COINS.level_50,
  },
];

// === Local storage helpers ===

function getLocalAchievements(): AchievementsData {
  try {
    const raw = localStorage.getItem('sbarazzo_achievements');
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function setLocalAchievements(data: AchievementsData) {
  try {
    localStorage.setItem('sbarazzo_achievements', JSON.stringify(data));
  } catch { /* ignore */ }
}

// === Hook ===

export interface UseAchievementsReturn {
  achievements: AchievementsData;
  loading: boolean;
  checkCondition: (conditionType: AchievementConditionType, value: number) => boolean;
  checkAllConditions: (matchData: Record<AchievementConditionType, number>) => AchievementDef[];
  getAchievementDef: (id: string) => AchievementDef | undefined;
  totalEarnedCoins: number;
  saveAchievements: (ids: string[]) => Promise<void>;
  checkAndSave: (matchData: Partial<Record<AchievementConditionType, number>>) => Promise<AchievementDef[]>;
}

export function useAchievements(user: User | null): UseAchievementsReturn {
  const [achievements, setAchievements] = useState<AchievementsData>(getLocalAchievements);
  const [loading, setLoading] = useState(true);
  const isInitialized = useRef(false);

  // Sync from Firebase on mount — merge Firebase data with local (Firebase wins for conflicts)
  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    if (!user) {
      setAchievements(getLocalAchievements());
      setLoading(false);
      return;
    }

    const achievementsRef = ref(db, `users/${user.uid}/achievements`);
    const unsub = onValue(achievementsRef, (snapshot) => {
      const fbData: AchievementsData = snapshot.val() ?? {};
      const localData = getLocalAchievements();

      // Merge: start with local, then overlay Firebase (Firebase wins for same-key entries)
      const merged: AchievementsData = { ...localData };
      for (const [id, unlockedAt] of Object.entries(fbData)) {
        if (unlockedAt) {
          merged[id] = unlockedAt;
        }
      }

      setAchievements(merged);
      setLocalAchievements(merged);

      // If Firebase is empty, initialize the node so it exists for future writes
      if (Object.keys(fbData).length === 0) {
        set(ref(db, `users/${user.uid}/achievements`), merged).catch(() => {});
      }
      setLoading(false);
    }, (err) => {
      console.warn('[useAchievements] Firebase read error, falling back to local:', err.message);
      setAchievements(getLocalAchievements());
      setLoading(false);
    });

    return () => unsub();
  }, [user]);

  /**
   * Check if a single condition is met for a given achievement.
   * Returns true if the condition satisfies the achievement's requirement.
   */
  const checkCondition = useCallback(
    (conditionType: AchievementConditionType, value: number): boolean => {
      const def = ACHIEVEMENTS.find(
        (a) => a.conditionType === conditionType && !achievements[a.id]
      );
      if (!def) return false;
      return value >= def.conditionValue;
    },
    [achievements]
  );

  /**
   * Check multiple conditions at once and return all newly unlocked achievements.
   * matchData maps conditionType -> current value from player stats.
   */
  const checkAllConditions = useCallback(
    (matchData: Record<AchievementConditionType, number>): AchievementDef[] => {
      const newlyUnlocked: AchievementDef[] = [];
      for (const def of ACHIEVEMENTS) {
        if (achievements[def.id]) continue; // already earned
        const currentValue = matchData[def.conditionType] ?? 0;
        if (currentValue >= def.conditionValue) {
          newlyUnlocked.push(def);
        }
      }
      return newlyUnlocked;
    },
    [achievements]
  );

  const getAchievementDef = useCallback(
    (id: string): AchievementDef | undefined => ACHIEVEMENTS.find((a) => a.id === id),
    []
  );

  // Save newly unlocked achievement IDs to Firebase (individual paths) and local state
  const saveAchievements = useCallback(
    async (ids: string[]): Promise<void> => {
      if (ids.length === 0) return;
      const now = Date.now();
      const updated: AchievementsData = { ...achievements };
      for (const id of ids) {
        updated[id] = now;
      }
      // Update local state
      setAchievements(updated);
      setLocalAchievements(updated);
      // Persist each achievement to its own Firebase path: users/{uid}/achievements/{id}
      if (user) {
        try {
          const promises = ids.map((id) =>
            set(ref(db, `users/${user.uid}/achievements/${id}`), { unlockedAt: now })
          );
          await Promise.all(promises);
        } catch (err) {
          console.warn('[useAchievements] Failed to save to Firebase, keeping local:', err);
        }
      }
    },
    [achievements, user]
  );

  // Convenience: check conditions and immediately save any newly unlocked achievements
  const checkAndSave = useCallback(
    async (matchData: Partial<Record<AchievementConditionType, number>>): Promise<AchievementDef[]> => {
      const newlyUnlocked = checkAllConditions(matchData as Record<AchievementConditionType, number>);
      if (newlyUnlocked.length > 0) {
        await saveAchievements(newlyUnlocked.map((def) => def.id));
      }
      return newlyUnlocked;
    },
    [checkAllConditions, saveAchievements]
  );

  const totalEarnedCoins = ACHIEVEMENTS.reduce((sum, def) => {
    return achievements[def.id] ? sum + def.coinReward : sum;
  }, 0);

  return {
    achievements,
    loading,
    checkCondition,
    checkAllConditions,
    getAchievementDef,
    totalEarnedCoins,
    saveAchievements,
    checkAndSave,
  };
}